import React, {useState, useRef, useMemo} from 'react'
import {Box, Typography, Popover, MenuItem} from '@mui/material'
import {ChevronDown} from 'lucide-react'
import {useNavigate, useLocation} from 'react-router-dom'
import styled from 'styled-components'
import {SIDEBAR_ROUTES} from '~/src/routes/constants'
import {usePermission} from '~/src/hooks/usePermission'

const GlobalButton = styled(Box)<{active?: boolean}>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: transparent;
  color: ${(props) => (props.active ? '#03A9FC' : '#334D6E')};

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #03a9fc;
  }
`

const StyledMenuItem = styled(MenuItem)<{active?: boolean}>`
  && {
    padding: 12px 16px;
    font-size: 14px;
    color: ${(props) => (props.active ? '#03A9FC' : 'var(--neutral-700)')};
    background: ${(props) => (props.active ? 'rgba(3, 169, 252, 0.08)' : 'transparent')};
    min-width: 180px;
    font-weight: ${(props) => (props.active ? '600' : '400')};

    &:hover {
      background: var(--primary-50);
      color: var(--primary-600);
    }
  }
`

const ChevronIcon = styled(ChevronDown)<{open?: boolean}>`
  width: 16px;
  height: 16px;
  margin-left: 4px;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.open ? 'rotate(180deg)' : 'rotate(0deg)')};
`

interface GlobalDropdownProps {
  active?: boolean
}

export const GlobalDropdown: React.FC<GlobalDropdownProps> = ({active = false}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const buttonRef = useRef<HTMLDivElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setIsOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsOpen(false)
  }

  const navigateTo = (path: string) => {
    navigate(path)
    handleClose()
  }

  // Get permissions for global submodules
  // All global settings use 'global' as the module and the specific setting as submodule
  const compensationCodesPermission = usePermission('global', 'compensation_codes')
  const wcCatalogsPermission = usePermission('global', 'wc_catalogs')
  const paycodesPermission = usePermission('global', 'paycodes')
  const authoritiesPermission = usePermission('global', 'authorities')
  const taxPermission = usePermission('global', 'tax')
  const metricsPermission = usePermission('global', 'metrics')
  const rolesPermissionsPermission = usePermission('global', 'roles_permissions')
  const generateReportPermission = usePermission('global', 'generate_report')
  // TODO: Replace 'activity_logs' with actual permission key when BE implements it
  const activityLogsPermission = usePermission('global', 'activity_logs')

  // Filter menu items based on permissions
  const globalMenuItems = useMemo(() => {
    const allItems = [
      {
        label: 'Workers Compensation Codes',
        path: SIDEBAR_ROUTES.GLOBAL_COMPENSATION_CODES,
        canView: compensationCodesPermission.canView,
      },
      {
        label: 'WC Catalogs',
        path: SIDEBAR_ROUTES.GLOBAL_WC_CATALOGS,
        canView: wcCatalogsPermission.canView,
      },
      {
        label: 'Paycodes',
        path: SIDEBAR_ROUTES.GLOBAL_PAYCODES,
        canView: paycodesPermission.canView,
      },
      {
        label: 'Authorities',
        path: SIDEBAR_ROUTES.GLOBAL_AUTHORITIES,
        canView: authoritiesPermission.canView,
      },
      {
        label: 'Tax',
        path: SIDEBAR_ROUTES.GLOBAL_TAX,
        canView: taxPermission.canView,
      },
      {
        label: 'Metrics',
        path: SIDEBAR_ROUTES.GLOBAL_METRICS,
        canView: metricsPermission.canView,
      },
      {
        label: 'Roles and Permissions',
        path: '/roles-permissions',
        canView: rolesPermissionsPermission.canView,
      },
      {
        label: 'Data Migration',
        path: SIDEBAR_ROUTES.DATA_MIGRATION,
        canView: generateReportPermission.canView,
      },
      // TODO: Uncomment when BE implements activity_logs permission and we want to show page
      // {
      //   label: 'Activity Logs',
      //   path: SIDEBAR_ROUTES.GLOBAL_ACTIVITY_LOG,
      //   // TODO: Update canView when BE implements activity_logs permission
      //   // For now, show if user has any global access (platform admin)
      //   canView: activityLogsPermission.canView || rolesPermissionsPermission.canView,
      // },
    ]
    return allItems.filter((item) => item.canView)
  }, [
    compensationCodesPermission.canView,
    wcCatalogsPermission.canView,
    paycodesPermission.canView,
    authoritiesPermission.canView,
    taxPermission.canView,
    metricsPermission.canView,
    rolesPermissionsPermission.canView,
    generateReportPermission.canView,
    // TODO: Add activityLogsPermission.canView back when Activity Logs menu item is uncommented
  ])

  return (
    <>
      <GlobalButton ref={buttonRef} active={active || isOpen} onClick={handleClick}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            userSelect: 'none',
            color: 'inherit',
          }}>
          Global
        </Typography>
        <ChevronIcon open={isOpen} />
      </GlobalButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        elevation={0}
        sx={{
          '& .MuiPaper-root': {
            marginTop: '8px',
            borderRadius: '8px',
            border: '1px solid var(--neutral-200)',
            boxShadow: 'var(--shadow-strong)',
            background: 'white',
          },
        }}>
        {globalMenuItems.map((item) => {
          // Check if this menu item is currently active
          const isItemActive = location.pathname.includes(item.path)

          return (
            <StyledMenuItem
              key={item.path}
              onClick={() => navigateTo(item.path)}
              active={isItemActive}>
              {item.label}
            </StyledMenuItem>
          )
        })}
      </Popover>
    </>
  )
}

export default GlobalDropdown
